import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '../../context/DeviceContext';
import { useVirtualKeysSupport } from '../../utils/useVirtualKeysSupport';
import { formatConnectionLabel } from '../../utils/DeviceCatalog';
import './Sidebar.css';

const Sidebar = ({
  currentPath,
  collapsed = false,
  onToggleCollapse
}) => {
  const {
    isConnected,
    connectionType,
    protocolVersion,
    deviceInfo,
    activeAdapter,
    disconnectDevice,
  } = useDevice();
  const { visible: virtualKeysVisible } = useVirtualKeysSupport();
  const [showDevicePopup, setShowDevicePopup] = useState(false);
  const navigate = useNavigate();
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowDevicePopup(false);
      }
    };

    if (showDevicePopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDevicePopup]);

  const getPID = () => {
    if (deviceInfo?.productId) {
      return '0x' + deviceInfo.productId.toString(16).toUpperCase().padStart(4, '0');
    }
    return 'N/A';
  };

  const menuItems = [
    { label: '灵敏度', path: 'sensitivity', icon: 'dpi' },
    { label: '按键分配', path: 'keys', icon: 'key' },
    { label: '宏', path: 'macros', icon: 'macro' },
    {
      label: '滚轮',
      path: 'wheel',
      icon: 'wheel',
      capability: 'wheelSettings',
    },
    {
      label: '按键性能',
      path: 'hits',
      icon: 'hits',
      capability: 'hitsSettings',
    },
    {
      label: '虚拟按键',
      path: 'virtual-keys',
      icon: 'virtual',
      capability: 'hitsSettings',
      requireVirtualKeys: true,
    },
    {
      label: '行程校准',
      path: 'calibration',
      icon: 'calibrate',
      capability: 'hitsSettings',
      requireVirtualKeys: true,
    },
    {
      label: '灯光',
      path: 'lighting',
      icon: 'lighting',
      capability: 'lightingSettings',
    },
    { label: '鼠标设置', path: 'advanced', icon: 'advanced' },
    { label: '版本更新', path: 'firmware-update', icon: 'firmware' }
  ].filter((item) => {
    if (item.capability && activeAdapter?.capabilities?.[item.capability] !== true) {
      return false;
    }
    if (item.requireVirtualKeys && !virtualKeysVisible) return false;
    return true;
  });

  /** 返回连接页，重新选择设备 */
  const handleSwitchDevice = () => {
    setShowDevicePopup(false);
    disconnectDevice();
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo" title="WANI Hub">
          <span className="brand-logo">WANI Hub</span>
        </div>
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          title={collapsed ? '展开侧边栏' : '收起侧边栏'}
          aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
        >
          <span className={`collapse-chevron ${collapsed ? 'collapsed' : ''}`}>‹</span>
        </button>
      </div>

      <div className="device-info-wrapper" ref={popupRef}>
        <div
          className={`device-info ${showDevicePopup ? 'active' : ''}`}
          onClick={() => isConnected && setShowDevicePopup(!showDevicePopup)}
          title={deviceInfo?.productName || '未连接设备'}
        >
          <div className="device-avatar" aria-hidden>
            {deviceInfo?.image ? (
              <img src={deviceInfo.image} alt="" />
            ) : (
              (deviceInfo?.productName || '?').charAt(0).toUpperCase()
            )}
          </div>
          {!collapsed && (
            <div className="device-details">
              <div className="device-name">{deviceInfo?.productName || '未连接设备'}</div>
            </div>
          )}
          {!collapsed && isConnected && (
            <span className={`expand-icon ${showDevicePopup ? 'expanded' : ''}`}>‹</span>
          )}
        </div>

        {showDevicePopup && isConnected && (
          <div className={`device-popup ${collapsed ? 'from-collapsed' : ''}`}>
            <div className="popup-row">
              <span className="popup-label">型号</span>
              <span className="popup-value">{deviceInfo?.productName || 'N/A'}</span>
            </div>
            <div className="popup-row">
              <span className="popup-label">连接方式</span>
              <span className="popup-value">
                {formatConnectionLabel(connectionType)}
              </span>
            </div>
            <div className="popup-row">
              <span className="popup-label">PID</span>
              <span className="popup-value">{getPID()}</span>
            </div>
            <div className="popup-row">
              <span className="popup-label">协议版本</span>
              <span className="popup-value">{protocolVersion || 'N/A'}</span>
            </div>
            <button
              type="button"
              className="switch-device-button"
              onClick={handleSwitchDevice}
            >
              选择其他设备
            </button>
          </div>
        )}
      </div>

      <div className="menu-list">
        {menuItems.map((item) => {
          const active = currentPath === item.path;
          return (
            <div
              key={item.path}
              className={`menu-item ${active ? 'active' : ''}`}
              onClick={() => navigate(`/${item.path}`)}
              title={item.label}
            >
              <span className={`menu-icon icon-${item.icon}`} aria-hidden />
              {!collapsed && <span className="menu-label">{item.label}</span>}
            </div>
          );
        })}
      </div>

      <div className="sidebar-footer">
        <div
          className={`settings-item ${currentPath === 'settings' ? 'active' : ''}`}
          onClick={() => navigate('/settings')}
          title="设置"
        >
          <span className="menu-icon icon-gear" aria-hidden />
          {!collapsed && <span className="settings-label">设置</span>}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
